import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { useQueryClient } from "@tanstack/react-query";
import { mapDisputeApiToUi, mapSummaryToKpis } from "@/lib/mappers/disputes";
import type { DisputeUI, DisputesKpisUI } from "@/types/ui/disputes";
import type { 
  DisputesApiResponse, 
  DisputesDashboardApi,
  CreateDisputeRequest,
  UpdateDisputeRequest,
  CreateDisputeResponse,
  UpdateDisputeResponse,
  DisputeTypeApi,
} from "@/types/api/disputes";
import type { CustomersApiResponse } from "@/types/api/customers";
import type { UserApi } from "@/types/api/users";

export interface GetDisputesParams extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  priority?: string;
  disputeType?: string;
  customerId?: number;
  assignedToUserId?: number;
  amountMin?: number;
  amountMax?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

const mockDisputes: DisputeUI[] = [
  {
    id: "DSP-001",
    numericId: 1,
    customer: "Acme Corporation",
    customerId: 1,
    customerAccountCode: "ACM001",
    contactName: "Robert Anderson",
    contactEmail: "r.anderson@acmecorp.com",
    contactPhone: "+44 20 7123 4567",
    type: "Billing Error",
    amount: 4250.00,
    status: "In Progress",
    priority: "High",
    assignedTo: "John Smith",
    createdDate: "2024-01-15",
    dueDate: "2024-01-22",
    workflow: "Standard Billing Dispute",
    aiScore: 85,
    aiRecommendation: "Approve partial credit of 30% based on similar cases",
    currentLevel: 2,
    totalLevels: 4,
    description: "Customer disputes billing amount for services rendered in December. Claims that agreed-upon discount was not applied to the invoice.",
    resolutionNotes: "",
  },
  {
    id: "DSP-002",
    numericId: 2,
    customer: "TechStart Inc",
    customerId: 2,
    customerAccountCode: "TSI002",
    contactName: "Jennifer Martinez",
    contactEmail: "j.martinez@techstart.com",
    contactPhone: "+44 20 7234 5678",
    type: "Service Quality",
    amount: 10500.00,
    status: "Under Review",
    priority: "Critical",
    assignedTo: "Sarah Johnson",
    createdDate: "2024-01-14",
    dueDate: "2024-01-21",
    workflow: "Escalated Quality Dispute",
    aiScore: 72,
    aiRecommendation: "Escalate to senior management for review",
    currentLevel: 3,
    totalLevels: 4,
    description: "Major quality issues reported with delivered products. Customer requesting full refund and compensation.",
    resolutionNotes: "",
  },
  {
    id: "DSP-003",
    numericId: 3,
    customer: "Global Solutions Ltd",
    customerId: 3,
    customerAccountCode: "GSL003",
    contactName: "Michael Thompson",
    contactEmail: "m.thompson@globalsolutions.co.uk",
    contactPhone: "+44 20 7345 6789",
    type: "Payment Dispute",
    amount: 7200.00,
    status: "New",
    priority: "Medium",
    assignedTo: "Mike Chen",
    createdDate: "2024-01-16",
    dueDate: "2024-01-23",
    workflow: "Standard Payment Dispute",
    aiScore: 91,
    aiRecommendation: "Verify payment records and contact customer",
    currentLevel: 1,
    totalLevels: 4,
    description: "Customer claims payment was already made but invoice shows outstanding balance.",
    resolutionNotes: "",
  },
  {
    id: "DSP-004",
    numericId: 4,
    customer: "Enterprise Systems Co",
    customerId: 4,
    customerAccountCode: "ESC004",
    contactName: "Patricia Williams",
    contactEmail: "p.williams@entsystems.com",
    contactPhone: "+44 20 7456 7890",
    type: "Duplicate Charge",
    amount: 2650.00,
    status: "Resolved",
    priority: "Low",
    assignedTo: "Emily Davis",
    createdDate: "2024-01-10",
    dueDate: "2024-01-17",
    workflow: "Quick Resolution",
    aiScore: 95,
    aiRecommendation: "Issue credit immediately",
    currentLevel: 4,
    totalLevels: 4,
    description: "Duplicate invoice sent to customer. Credit issued and confirmed.",
    resolutionNotes: "Credit memo issued. Customer confirmed receipt and satisfaction.",
  },
  {
    id: "DSP-005",
    numericId: 5,
    customer: "Innovate Labs",
    customerId: 5,
    customerAccountCode: "INL005",
    contactName: "David Brown",
    contactEmail: "d.brown@innovatelabs.io",
    contactPhone: "+44 20 7567 8901",
    type: "Contract Terms",
    amount: 15750.00,
    status: "Escalated",
    priority: "High",
    assignedTo: "David Wilson",
    createdDate: "2024-01-12",
    dueDate: "2024-01-19",
    workflow: "Legal Review Required",
    aiScore: 68,
    aiRecommendation: "Consult legal team for contract interpretation",
    currentLevel: 3,
    totalLevels: 4,
    description: "Dispute over contract terms and pricing structure. Requires legal review.",
    resolutionNotes: "",
  },
];

function computeMockDisputesKpis(disputes: DisputeUI[]): DisputesKpisUI {
  const totalDisputes = disputes.length;
  const totalAmount = disputes.reduce((sum, d) => sum + d.amount, 0);
  const newCount = disputes.filter((d) => d.status === "New").length;
  const inProgressCount = disputes.filter((d) => d.status === "In Progress" || d.status === "Under Review").length;
  const resolvedCount = disputes.filter((d) => d.status === "Resolved" || d.status === "Closed").length;
  const highPriorityCount = disputes.filter((d) => d.priority === "High" || d.priority === "Critical").length;
  return { totalDisputes, totalAmount, newCount, inProgressCount, resolvedCount, highPriorityCount };
}

export function useDisputesList(params?: GetDisputesParams) {
  const { apiGet } = useApi();

  return useModeAwareQuery<DisputeUI[], Error>(
    ["disputes", params],
    async ({ signal }) => {
      const response = await apiGet<DisputesApiResponse>("/api/v1/disputes", params, signal);
      return response.data.map(mapDisputeApiToUi);
    },
    mockDisputes,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export function useDisputesDashboard() {
  const { apiGet } = useApi();
  const mockKpis = computeMockDisputesKpis(mockDisputes);

  return useModeAwareQuery<DisputesKpisUI, Error>(
    ["disputes-dashboard"],
    async ({ signal }) => {
      const response = await apiGet<DisputesDashboardApi>("/api/v1/disputes/dashboard", undefined, signal);
      return mapSummaryToKpis(response);
    },
    mockKpis,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export function useDisputesPageData(params?: GetDisputesParams) {
  const disputesQuery = useDisputesList(params);
  const dashboardQuery = useDisputesDashboard();

  return {
    disputes: disputesQuery.data ?? [],
    kpis: dashboardQuery.data ?? {
      totalDisputes: 0,
      totalAmount: 0,
      newCount: 0,
      inProgressCount: 0,
      resolvedCount: 0,
      highPriorityCount: 0,
    },
    isLoading: disputesQuery.isLoading || dashboardQuery.isLoading,
    error: disputesQuery.error || dashboardQuery.error,
    refetch: () => {
      disputesQuery.refetch();
      dashboardQuery.refetch();
    },
  };
}

export function useCreateDispute() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CreateDisputeResponse, Error, CreateDisputeRequest>(
    async (data) => {
      return apiPost<CreateDisputeResponse>("/api/v1/disputes", data);
    },
    async () => {
      return {
        id: Date.now(),
        disputeNumber: `DSP-${String(Date.now()).slice(-3)}`,
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["disputes"] });
        queryClient.invalidateQueries({ queryKey: ["disputes-dashboard"] });
      },
    }
  );
}

export function useUpdateDispute(disputeId: number) {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<UpdateDisputeResponse, Error, UpdateDisputeRequest>(
    async (data) => {
      return apiPut<UpdateDisputeResponse>(`/api/v1/disputes/${disputeId}`, data);
    },
    async () => {
      return {
        id: disputeId,
        disputeNumber: `DSP-${String(disputeId).padStart(3, "0")}`,
        message: "Dispute updated successfully",
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["disputes"] });
        queryClient.invalidateQueries({ queryKey: ["disputes-dashboard"] });
      },
    }
  );
}

export interface CustomerOption {
  id: number;
  name: string;
}

export interface DisputeTypeOption {
  id: number;
  name: string;
}

export interface UserOption {
  id: number;
  name: string;
}

const mockCustomerOptions: CustomerOption[] = [
  { id: 1, name: "Acme Corporation" },
  { id: 2, name: "TechStart Inc" },
  { id: 3, name: "Global Solutions Ltd" },
  { id: 4, name: "Enterprise Systems Co" },
  { id: 5, name: "Innovate Labs" },
];

const mockDisputeTypeOptions: DisputeTypeOption[] = [
  { id: 1, name: "Billing Error" },
  { id: 2, name: "Service Quality" },
  { id: 3, name: "Payment Dispute" },
  { id: 4, name: "Duplicate Charge" },
  { id: 5, name: "Contract Terms" },
];

const mockUserOptions: UserOption[] = [
  { id: 1, name: "John Smith" },
  { id: 2, name: "Sarah Johnson" },
  { id: 3, name: "Mike Chen" },
  { id: 4, name: "Emily Davis" },
  { id: 5, name: "David Wilson" },
];

export function useCustomerOptions() {
  const { apiGet } = useApi();

  return useModeAwareQuery<CustomerOption[], Error>(
    ["customer-options"],
    async ({ signal }) => {
      const response = await apiGet<CustomersApiResponse>("/api/v1/customers", { pageSize: 1000 }, signal);
      return response.data.map((c) => ({ id: parseInt(c.id), name: c.name }));
    },
    mockCustomerOptions,
    { staleTime: 5 * 60 * 1000 }
  );
}

export function useDisputeTypeOptions() {
  const { apiGet } = useApi();

  return useModeAwareQuery<DisputeTypeOption[], Error>(
    ["dispute-type-options"],
    async ({ signal }) => {
      const response = await apiGet<DisputeTypeApi[]>("/api/v1/disputes/types", undefined, signal);
      return response.map((t) => ({ id: t.id, name: t.name }));
    },
    mockDisputeTypeOptions,
    { staleTime: 5 * 60 * 1000 }
  );
}

export function useUserOptions() {
  const { apiGet } = useApi();

  return useModeAwareQuery<UserOption[], Error>(
    ["user-options"],
    async ({ signal }) => {
      const response = await apiGet<UserApi[]>("/api/v1/users", undefined, signal);
      return response.map((u) => ({ id: u.id, name: `${u.firstName} ${u.lastName}` }));
    },
    mockUserOptions,
    { staleTime: 5 * 60 * 1000 }
  );
}
